import katex from 'katex'
import 'katex/dist/katex.min.css'
import DOMPurify from 'dompurify'

interface MathTextProps {
  children: string
  className?: string
}

// Splits text into alternating plain-text and math segments.
// Handles: $$...$$, $...$, \[...\], \(...\)
function splitMath(text: string): Array<{ type: 'text' | 'block-math' | 'inline-math'; value: string }> {
  const parts: Array<{ type: 'text' | 'block-math' | 'inline-math'; value: string }> = []
  // Order matters: match $$...$$ and \[...\] before $...$ and \(...\)
  const re = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$\n]+?\$|\\\([\s\S]+?\\\))/g
  let last = 0
  let m: RegExpExecArray | null

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push({ type: 'text', value: text.slice(last, m.index) })
    }
    const raw = m[1]
    const isBlock = raw.startsWith('$$') || raw.startsWith('\\[')
    const inner = raw
      .replace(/^\$\$|\$\$$/g, '')
      .replace(/^\\\[|\\\]$/g, '')
      .replace(/^\$|\$$/g, '')
      .replace(/^\\\(|\\\)$/g, '')
    parts.push({ type: isBlock ? 'block-math' : 'inline-math', value: inner })
    last = m.index + raw.length
  }

  if (last < text.length) {
    parts.push({ type: 'text', value: text.slice(last) })
  }

  return parts
}

function renderMath(latex: string, displayMode: boolean): string {
  try {
    const html = katex.renderToString(latex, { displayMode, throwOnError: false, output: 'html' })
    // RT-88 (Story #4862): this is the one HTML sink the sanitisation sweep had
    // not closed. KaTeX escapes its own input, so this is defence in depth, not
    // a live XSS fix — but it removes the dependency on that escaping staying
    // correct across KaTeX upgrades. DOMPurify keeps KaTeX's spans, classes and
    // (CSS-sanitised) inline styles, which the layout needs, while stripping any
    // script/handler a future regression could emit.
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true, mathMl: true, svg: true } })
  } catch {
    return latex
  }
}

export default function MathText({ children, className }: MathTextProps) {
  const segments = splitMath(children)

  return (
    <span className={className}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') return seg.value
        const html = renderMath(seg.value, seg.type === 'block-math')
        return (
          <span
            key={i}
            dangerouslySetInnerHTML={{ __html: html }}
            style={seg.type === 'block-math' ? { display: 'block', overflowX: 'auto' } : undefined}
          />
        )
      })}
    </span>
  )
}
